import { Spin } from 'antd'
import { useDebounceFn } from 'ahooks'
import { Trans } from '@lingui/react/macro'

import { getLeafNodes, searchLeafNodes } from '../_hooks/useLabelsTree'

import CategoryTabs from './CategoryTabs'
import SubCategoryTabs from './SubCategoryTabs'
import TagGrid from './TagGrid'
import SearchInput from './SearchInput'
import AudioPreview from './AudioPreview'

import type { PresetLabelTreeNode } from '@loc/server/interface/preset'

import TipsBaseModal from '@/pages/tad.ai/_blocks/Modals/Tips/TipsBaseModal'
import ScrollArea from '@/pages/tad.ai/_components/ScrollArea'
import { CssIcon } from '@/components/CssIcon'


import { useState, useEffect } from 'react';
import { cls } from 'tagged-classnames-free';
interface StyleModalProps {
  open: boolean
  onClose: () => void
  tree: PresetLabelTreeNode[]
  isLoading: boolean
  selectedTags: string[]
  onTagToggle: (tag: PresetLabelTreeNode) => void
}

function StyleModal(props: StyleModalProps) {
  const { open, onClose, tree, isLoading, selectedTags, onTagToggle } = props

  const [activeCategory, setActiveCategory] = useState<string>('')
  const [activeSubCategory, setActiveSubCategory] = useState<string>('')
  const [searchValue, setSearchValue] = useState('')
  const [searchResults, setSearchResults] = useState<
    PresetLabelTreeNode[] | null
  >(null)
  const [isSearching, setIsSearching] = useState(false)
  const [auditionUrl, setAuditionUrl] = useState('')
  const [auditionLabel, setAuditionLabel] = useState('')

  // 当前一级分类节点及其二级子分类
  const activeNode = tree.find((n) => n.code === activeCategory)
  const subCategories = activeNode?.children ?? []

  // 默认选中第一个一级分类
  useEffect(() => {
    if (tree.length > 0 && !activeCategory) {
      setActiveCategory(tree[0].code)
    }
  }, [tree, activeCategory])

  // 一级分类变化时若二级未设置，默认选中第一个
  useEffect(() => {
    if (subCategories.length > 0 && !activeSubCategory) {
      setActiveSubCategory(subCategories[0].code)
    }
  }, [subCategories, activeSubCategory])

  // 弹窗打开时重置
  useEffect(() => {
    if (open) {
      setSearchValue('')
      setSearchResults(null)
      setIsSearching(false)
      setAuditionUrl('')
      setAuditionLabel('')
    }
  }, [open])

  // 选中项全部清空时，重置试听状态
  useEffect(() => {
    if (selectedTags.length === 0) {
      setAuditionUrl('')
      setAuditionLabel('')
    }
  }, [selectedTags])

  // 搜索（300ms debounce）
  const { run: doSearch, cancel: cancelSearch } = useDebounceFn(
    (keyword: string) => {
      if (!keyword.trim()) {
        setSearchResults(null)
        setIsSearching(false)
        return
      }
      const results = searchLeafNodes(tree, keyword)
      setSearchResults(results)
      setIsSearching(false)
    },
    { wait: 300 },
  )

  const handleSearchChange = (value: string) => {
    setSearchValue(value)
    if (!value.trim()) {
      cancelSearch()
      setSearchResults(null)
      setIsSearching(false)
      return
    }
    setIsSearching(true)
    doSearch(value)
  }

  const handleCategoryChange = (key: string) => {
    setActiveCategory(key)
    // 直接选中新分类的第一个二级子分类，避免闪烁
    const newNode = tree.find((n) => n.code === key)
    setActiveSubCategory(newNode?.children?.[0]?.code ?? '')
  }

  const handleAuditionPlay = (
    tag: PresetLabelTreeNode,
    isSelecting: boolean,
  ) => {
    if (isSelecting && tag.audition) {
      // 选中 → 设置试听数据
      setAuditionUrl(tag.audition)
      setAuditionLabel(tag.displayName)
    } else if (!isSelecting) {
      // 取消选中 → 清除试听数据
      setAuditionUrl('')
      setAuditionLabel('')
    }
  }

  // 当前一级分类下所有叶子标签
  const allLeafTags = activeNode ? getLeafNodes([activeNode]) : []
  // 叶子总数不超过 10 个时，隐藏二级分类，直接展示所有三级标签
  const shouldHideSubCategory = allLeafTags.length <= 10

  // 当前展示的标签：隐藏二级时展示全部，否则展示当前二级分类下的
  const activeSubNode = subCategories.find((n) => n.code === activeSubCategory)
  const currentTags = shouldHideSubCategory
    ? allLeafTags
    : activeSubNode
      ? getLeafNodes([activeSubNode])
      : []

  // 渲染标签内容区
  const renderContent = () => {
    if (isLoading || isSearching) {
      return (
        <div
          className={cls`flex min-h-full flex-1 items-center justify-center`}
        >
          <Spin />
        </div>
      )
    }

    // 搜索结果
    if (searchResults !== null) {
      if (searchResults.length === 0) {
        return (
          <div
            className={cls`flex min-h-full flex-1 flex-col items-center justify-center gap-3`}
          >
            <CssIcon
              className={cls`i-cus--tad-no-linear text-f-text-quaternary size-12`}
            />
            <div
              className={cls`text-f-text-quaternary text-center text-sm leading-relaxed`}
            >
              <Trans>
                We couldn't find any results. Please try different keywords.
              </Trans>
            </div>
          </div>
        )
      }
      return (
        <TagGrid
          tags={searchResults}
          selectedTags={selectedTags}
          onTagClick={onTagToggle}
          onAuditionPlay={handleAuditionPlay}
          className='pt-2'
        />
      )
    }

    // 正常分类展示：三级标签网格（二级 Tab 已提到滚动区域外）
    return (
      <TagGrid
        tags={currentTags}
        selectedTags={selectedTags}
        onTagClick={onTagToggle}
        onAuditionPlay={handleAuditionPlay}
        className='pt-4'
      />
    )
  }

  return (
    <TipsBaseModal
      open={open}
      onCancel={onClose}
      destroyOnHidden
      width={700}
      maskClosable
      closable={false}
    >
      <div className={cls`flex flex-col gap-4`}>
        {/* 顶部：试听预览 + 搜索框 + 关闭按钮 */}
        <div className={cls`flex items-center justify-between gap-2`}>
          <AudioPreview
            key={auditionUrl}
            src={auditionUrl}
            label={auditionLabel}
          />
          <div className={cls`flex shrink-0 items-center gap-2`}>
            <SearchInput
              value={searchValue}
              onChange={handleSearchChange}
              className={cls`hidden w-64 md:flex`}
            />
            <button
              type='button'
              className={cls`text-f-text-quaternary hover:text-f-text flex size-6 cursor-pointer items-center justify-center`}
              onClick={onClose}
            >
              <CssIcon className={cls`i-com--x size-5`} />
            </button>
          </div>
        </div>
        {/* 移动端搜索框单独一行 */}
        <div className={cls`md:hidden`}>
          <SearchInput
            value={searchValue}
            onChange={handleSearchChange}
            className={cls`w-full`}
          />
        </div>

        <div className={cls`flex h-[45vh] md:h-[346px]`}>
          {/* 左侧：一级分类侧边栏，撑满高度超出滚动 */}
          {!searchValue && (
            <ScrollArea
              scrollbarVisibility='hoverOrScroll'
              className={cls`border-f-border-secondary max-w-[100px] shrink-0 border-r pr-4 md:max-w-[180px]`}
            >
              <CategoryTabs
                categories={tree}
                activeKey={activeCategory}
                onChange={handleCategoryChange}
              />
            </ScrollArea>
          )}

          {/* 右侧：二级 Tab + 三级标签 */}
          <div
            className={cls`flex min-w-0 flex-1 flex-col ${!searchValue ? 'pl-4' : ''}`}
          >
            {/* 二级 Tab 固定不滚动（叶子总数 ≤ 10 时隐藏） */}
            {!searchValue &&
              !shouldHideSubCategory &&
              subCategories.length > 0 && (
                <div className={cls`shrink-0`}>
                  <SubCategoryTabs
                    categories={subCategories}
                    activeKey={activeSubCategory}
                    onChange={setActiveSubCategory}
                  />
                </div>
              )}
            {/* 三级标签撑满剩余高度，超出滚动 */}
            <ScrollArea
              scrollbarVisibility='hoverOrScroll'
              className={cls`min-h-0 flex-1`}
              classNames={{ content: 'min-h-full flex flex-col' }}
            >
              {renderContent()}
            </ScrollArea>
          </div>
        </div>
      </div>
    </TipsBaseModal>
  )
}

export default StyleModal
